import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import searchService from '../../services/searchService.js';

const SearchDropdown = ({ isOpen, onClose, onToggle }) => {
	const navigate = useNavigate();
	const [query, setQuery] = useState('');
	const [results, setResults] = useState([]);
	const [suggestions, setSuggestions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const inputRef = useRef(null);

  // Focus input when search opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Initialize search service and get suggestions
  useEffect(() => {
    if (isOpen) {
      searchService.initialize();
      if (!query.trim()) {
        setSuggestions(searchService.getSuggestions(5));
      }
    } else {
      // Reset state when dropdown closes
      setQuery('');
      setResults([]);
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  // Handle search query changes
  useEffect(() => {
    if (query.trim().length > 0) {
      setIsLoading(true);
      const timeoutId = setTimeout(() => {
        const searchResults = searchService.search(query);
        setResults(searchResults);
        setIsLoading(false);
        setSelectedIndex(-1);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setResults([]);
      setIsLoading(false);
      setSelectedIndex(-1);
      if (isOpen) {
        setSuggestions(searchService.getSuggestions(5));
      }
    }
  }, [query, isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const totalItems = results.length + suggestions.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < totalItems - 1 ? prev + 1 : -1
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > -1 ? prev - 1 : totalItems - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < results.length) {
            // Selected item is from search results
            const selectedItem = results[selectedIndex];
            handleItemClick(selectedItem);
          } else {
            // Selected item is from suggestions
            const suggestionIndex = selectedIndex - results.length;
            const selectedSuggestion = suggestions[suggestionIndex];
            if (selectedSuggestion) {
              setQuery(selectedSuggestion.query || selectedSuggestion.title);
            }
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  // Handle item click
  const handleItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
      onClose();
    } else if (item.action) {
      item.action();
      onClose();
    } else if (item.query) {
      // Handle suggestion click
      setQuery(item.query);
    } else if (typeof item === 'string') {
      // Handle string suggestion
      setQuery(item);
    }
  };

  // Group results by category
  const groupedResults = results.reduce((acc, item) => {
    const category = item.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  // Get category icon
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'navigation': return 'bi-compass';
      case 'features': return 'bi-gear';
      case 'actions': return 'bi-lightning';
      case 'help': return 'bi-question-circle';
      default: return 'bi-search';
    }
  };

  // Highlight matching text
  const highlightText = (text, query) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        <mark key={index} className="bg-warning text-dark">{part}</mark> : 
        part
    );
  };



  if (!isOpen) return null;

  let globalIndex = 0;

  return (
    <div
      className="dropdown-menu show p-0 shadow-lg"
      style={{
        position: "absolute",
        right: 0,
        top: "100%",
        minWidth: 320,
        maxWidth: 400,
        zIndex: 1050,
        maxHeight: 400,
        overflow: "auto",
      }}
    >
      
      {/* Search Input */}
      <div className="p-3 border-bottom">
        <div className="position-relative">
          <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
          <input
            ref={inputRef}
            type="text"
            className="form-control ps-5 pe-4"
            placeholder="Search pages, features, help..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {query && (
            <button
              type="button"
              className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 p-1"
              onClick={() => setQuery('')}
            >
              <i className="bi bi-x" style={{ fontSize: '12px' }}></i>
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="p-3 text-center">
          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
          <span className="text-muted">Searching...</span>
        </div>
      )}

      {/* Search Results */}
      {!isLoading && results.length > 0 && (
        <div className="search-results" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {Object.entries(groupedResults).map(([category, items]) => (
            <div key={category}>
              <div className="px-3 py-2 bg-light border-bottom">
                <small className="text-muted fw-semibold text-uppercase">
                  <i className={`bi ${getCategoryIcon(category)} me-1`}></i>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </small>
              </div>
              {items.map((item, index) => {
                const globalIndex = results.indexOf(item);
                return (
                  <div
                    key={`${category}-${index}`}
                    className={`px-3 py-2 border-bottom cursor-pointer ${
                      selectedIndex === globalIndex ? 'bg-primary text-white' : 'hover-bg-light'
                    }`}
                    onClick={() => handleItemClick(item)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center">
                      {item.icon && (
                        <i className={`bi ${item.icon} me-2 ${selectedIndex === globalIndex ? 'text-white' : 'text-primary'}`}></i>
                      )}
                      <div className="flex-grow-1">
                        <div className="fw-medium">
                          {highlightText(item.title, query)}
                        </div>
                        {item.description && (
                          <small className={selectedIndex === globalIndex ? 'text-white-50' : 'text-muted'}>
                            {highlightText(item.description, query)}
                          </small>
                        )}
                      </div>
                      {item.score && (
                        <small className={`badge ${selectedIndex === globalIndex ? 'bg-light text-dark' : 'bg-light text-muted'}`}>
                          {Math.round(item.score * 100)}%
                        </small>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Suggestions */}
      {!isLoading && !query.trim() && suggestions.length > 0 && (
        <div className="search-suggestions">
          <div className="px-3 py-2 bg-light border-bottom">
            <small className="text-muted fw-semibold text-uppercase">
              <i className="bi bi-lightbulb me-1"></i>
              Suggestions
            </small>
          </div>
          {suggestions.map((suggestion, index) => {
            const globalIndex = results.length + index;
            return (
              <div
                key={`suggestion-${index}`}
                className={`px-3 py-2 border-bottom cursor-pointer ${
                  selectedIndex === globalIndex ? 'bg-primary text-white' : 'hover-bg-light'
                }`}
                onClick={() => handleItemClick(suggestion)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex align-items-center">
                  <i className={`bi bi-search me-2 ${selectedIndex === globalIndex ? 'text-white' : 'text-muted'}`}></i>
                  <span>{suggestion.title || suggestion.query || suggestion}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}



      {/* No Results */}
      {!isLoading && query.trim() && results.length === 0 && (
        <div className="p-4 text-center">
          <i className="bi bi-search fs-1 text-muted mb-2 d-block"></i>
          <div className="text-muted">No results found for "{query}"</div>
          <small className="text-muted">Try different keywords or check your spelling</small>
        </div>
      )}

      {/* Footer */}
      <div className="px-3 py-2 bg-light border-top">
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            <i className="bi bi-arrow-up-down me-1"></i>
            Use ↑↓ to navigate, Enter to select, Esc to close
          </small>
          <small className="text-muted">
            {results.length > 0 && `${results.length} result${results.length !== 1 ? 's' : ''}`}
          </small>
        </div>
      </div>
    </div>
  );
};

export default SearchDropdown;